import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

function TrainingPrograms() {
  const [companyLanguage, setCompanyLanguage] = useState('en');

  useEffect(() => {
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);
    
    const handleStorageChange = () => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      setCompanyLanguage(updatedLang);
    };
    
    window.addEventListener('companyDataUpdated', handleStorageChange);
    
    const interval = setInterval(() => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      if (updatedLang !== companyLanguage) {
        setCompanyLanguage(updatedLang);
      }
    }, 2000);

    return () => {
      window.removeEventListener('companyDataUpdated', handleStorageChange);
      clearInterval(interval);
    };
  }, [companyLanguage]);

  const isUrdu = companyLanguage === 'ur';

  return (
    <>
      <Helmet>
        <title>{isUrdu ? 'ٹریننگ پروگرامز - Galla Mandi Bills & Khata' : 'Training Programs - Galla Mandi Bills & Khata'}</title>
      </Helmet>
      
      <div
        className={`${isUrdu ? 'font-urdu' : ''} bg-brand-background min-h-screen`}
        style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
        dir={isUrdu ? 'rtl' : 'ltr'}
      >
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-brand-header via-green-700 to-brand-header text-white py-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-72 h-72 bg-green-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
            <div className="absolute top-0 right-0 w-72 h-72 bg-green-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-green-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
          </div>
          <div className={`container mx-auto px-4 text-center relative z-10 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
            <h1 className="text-5xl md:text-6xl font-bold mb-4 animate-fade-in-up">
              {isUrdu ? 'ٹریننگ پروگرامز' : 'Training Programs'}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 font-medium">
              {isUrdu 
                ? 'مہارت اور علم میں اضافہ کے لیے جامع تربیتی پروگرام' 
                : 'Comprehensive training programs to enhance skills and knowledge'}
            </p>
          </div>
        </section>

        {/* Programs Section */}
        <section className={`py-20 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-brand-header mb-4">
                {isUrdu ? 'ہمارے تربیتی پروگرام' : 'Our Training Programs'}
              </h2>
              <p className="text-gray-600 text-lg">
                {isUrdu ? 'مہارت کی ترقی کے لیے مختلف کورسز' : 'Various courses for skill development'}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: "📖",
                  title: isUrdu ? "بنیادی تربیت" : "Basic Training",
                  titleUrdu: "بنیادی تربیت",
                  desc: isUrdu ? "نئے صارفین کے لیے بنیادی گائیڈ" : "Basic guide for new users",
                  descUrdu: "نئے صارفین کے لیے بنیادی گائیڈ"
                },
                {
                  icon: "🎓",
                  title: isUrdu ? "اعلیٰ تربیت" : "Advanced Training",
                  titleUrdu: "اعلیٰ تربیت",
                  desc: isUrdu ? "تجربہ کار صارفین کے لیے اعلیٰ سطح کی تربیت" : "Advanced level training for experienced users",
                  descUrdu: "تجربہ کار صارفین کے لیے اعلیٰ سطح کی تربیت"
                },
                {
                  icon: "💼",
                  title: isUrdu ? "کاروباری تربیت" : "Business Training",
                  titleUrdu: "کاروباری تربیت",
                  desc: isUrdu ? "کاروبار کو بہتر بنانے کے طریقے" : "Ways to improve your business",
                  descUrdu: "کاروبار کو بہتر بنانے کے طریقے"
                },
                {
                  icon: "🖥️",
                  title: isUrdu ? "آن لائن کورسز" : "Online Courses",
                  titleUrdu: "آن لائن کورسز",
                  desc: isUrdu ? "کہیں سے بھی سیکھیں" : "Learn from anywhere",
                  descUrdu: "کہیں سے بھی سیکھیں"
                },
                {
                  icon: "👥",
                  title: isUrdu ? "گروپ سیشنز" : "Group Sessions",
                  titleUrdu: "گروپ سیشنز",
                  desc: isUrdu ? "دوسروں کے ساتھ مل کر سیکھیں" : "Learn together with others",
                  descUrdu: "دوسروں کے ساتھ مل کر سیکھیں"
                },
                {
                  icon: "📜",
                  title: isUrdu ? "سرٹیفکیٹ" : "Certification",
                  titleUrdu: "سرٹیفکیٹ",
                  desc: isUrdu ? "کامیابی پر سرٹیفکیٹ حاصل کریں" : "Get certified upon completion",
                  descUrdu: "کامیابی پر سرٹیفکیٹ حاصل کریں"
                }
              ].map((program, idx) => (
                <div
                  key={idx}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group"
                >
                  <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {program.icon}
                  </div>
                  <h3 className={`text-2xl font-bold text-gray-900 mb-3 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? program.titleUrdu : program.title}
                  </h3>
                  <p className={`text-gray-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? program.descUrdu : program.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-brand-header to-green-700 text-white">
          <div className={`container mx-auto px-4 text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
            <h2 className="text-4xl font-bold mb-4">
              {isUrdu ? 'ابھی شروع کریں' : 'Get Started Today'}
            </h2>
            <p className="text-xl mb-8 text-white/90">
              {isUrdu ? 'اپنی مہارت کو بڑھانے کے لیے آج ہی شامل ہوں' : 'Join today to enhance your skills'}
            </p>
            <Link
              to="/contact"
              className="bg-brand-button hover:bg-yellow-300 text-gray-900 font-bold px-8 py-4 rounded-xl transition transform hover:scale-105 hover:shadow-lg inline-block"
            >
              {isUrdu ? 'رابطہ کریں' : 'Contact Us'}
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}

export default TrainingPrograms;

